import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ReportService } from '../services/ReportService'
import { useAuthStore } from './auth'
import type { Report } from '../types/report'

export const useReportStore = defineStore('report', () => {
  // State
  const reports = ref<Report[]>([])
  const currentReport = ref<Report | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Services
  const reportService = new ReportService()
  const authStore = useAuthStore()

  // Helper function to update user quota
  const updateUserQuota = async () => {
    try {
      await authStore.fetchProfile()
      console.log('✅ Cuota actualizada automáticamente')
    } catch (error) {
      console.error('❌ Error actualizando cuota:', error)
    }
  }

  // Getters
  const hasReports = computed(() => reports.value.length > 0)
  const isLoading = computed(() => loading.value)
  const hasError = computed(() => error.value !== null)

  // Actions
  const initialize = () => {
    console.log('Report store initialized')
  }

  const uploadReport = async (file: File) => {
    loading.value = true
    error.value = null

    try {
      const result = await reportService.uploadReport(file)
      
      // Add to reports list
      reports.value.unshift(result)
      
      // Set as current report
      currentReport.value = result
      
      // Update user quota automatically
      await updateUserQuota()
      
      return result
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      loading.value = false
    }
  }

  const uploadReportV2 = async (file: File) => {
    loading.value = true
    error.value = null

    try {
      const result = await reportService.uploadReportV2(file)
      
      // Add to reports list
      reports.value.unshift(result)
      
      // Set as current report
      currentReport.value = result
      
      // Update user quota automatically
      await updateUserQuota()
      
      return result
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      loading.value = false
    }
  }

  const uploadReportV2WithPolling = async (
    file: File, 
    onProgress?: (progress: { step: number; agent: string; status: string }) => void
  ) => {
    loading.value = true
    error.value = null

    try {
      const result = await reportService.uploadReportV2WithPolling(file, onProgress)
      
      // Add to reports list
      reports.value.unshift(result)
      
      // Set as current report
      currentReport.value = result
      
      // Update user quota automatically
      await updateUserQuota()
      
      return result
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      loading.value = false
    }
  }

  const getReport = async (reportId: string) => {
    loading.value = true
    error.value = null

    try {
      const report = await reportService.getReport(reportId)
      currentReport.value = report
      return report
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      loading.value = false
    }
  }

  const listReports = async (page: number = 1, limit: number = 10, status?: string) => {
    loading.value = true
    error.value = null

    try {
      const result = await reportService.listReports(page, limit, status)
      reports.value = result.reports
      return result
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const clearCurrentReport = () => {
    currentReport.value = null
  }

  return {
    // State
    reports,
    currentReport,
    loading,
    error,
    
    // Getters
    hasReports,
    isLoading,
    hasError,
    
    // Actions
    initialize,
    uploadReport,
    uploadReportV2,
    uploadReportV2WithPolling,
    getReport,
    listReports,
    clearError,
    clearCurrentReport
  }
})
